import { useFetchCase } from "@/queries/useFetchCase.ts";
import { useFetchCasesForAutocomplete } from "@/queries/useFetchCasesForAutocomplete.ts";
import { Autocomplete, Box, TextField, Typography } from "@mui/material";
import { type ChangeEvent, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";

type Option = {
    id: string;
    name: string;
};

type Props = {
    limitToCurrentEmployee?: boolean;
    foundCaseId?: string;
};

const AssignCasesAutocomplete = ({ foundCaseId, limitToCurrentEmployee = false }: Props) => {
    const { watch, setValue, formState } = useFormContext();
    const formValue = watch("cases");
    const [searchString, setSearchString] = useState(foundCaseId ?? "");

    const {
        data: { data: caseData } = {},
    } = useFetchCase(foundCaseId);

    const {
        data: { data } = {},
        isError,
        isRefetching,
        isLoading,
    } = useFetchCasesForAutocomplete({
        searchString: searchString,
        limitToCurrentEmployee,
    });

    const options: Option[] = useMemo(() => {
        let newOptions: Option[] = [];

        if (data) {
            newOptions = data.map((caseDocument) => ({
                id: caseDocument.idUUID,
                name: `#${caseDocument.id} / ${caseDocument.clientName}`,
            }));
        }

        if (caseData) {
            newOptions = newOptions.filter((option) => option.id !== caseData.idUUID);

            const newOption = {
                id: caseData.idUUID,
                name: `#${caseData.id} / ${caseData.clientName}`,
            };
            newOptions.push(newOption);

            setValue("cases", [newOption]);
        }

        return newOptions;
    }, [data, caseData, isRefetching]);

    useEffect(() => {
        if (foundCaseId && searchString === "") {
            setSearchString(foundCaseId);
        }
    }, [foundCaseId]);

    return (
        <>
            {isError && <Typography variant={"h5"}>Error loading cases.</Typography>}
            <Autocomplete
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label="Client name or Case ID"
                        value={searchString}
                        onChange={(event: ChangeEvent<HTMLInputElement>) => {
                            if (event.target.value.length >= 2) {
                                setSearchString(event.target.value);
                            }
                        }}
                    />
                )}
                options={options}
                loading={isLoading || isRefetching}
                getOptionLabel={(option: Option) => option.name ?? ""}
                onChange={(_event, value) => {
                    if (formValue?.find((option: Option) => option.id === value?.id)) {
                        return;
                    }

                    if (formValue && value) {
                        const newValues = [...formValue];
                        newValues.push(value);
                        setValue("cases", newValues);
                    }
                }}
                autoHighlight
                blurOnSelect
                sx={{
                    "& .MuiAutocomplete-inputRoot": {
                        padding: "4px!important",
                    },
                    "& .MuiAutocomplete-input": {
                        paddingTop: "4px!important",
                        paddingBottom: "4px!important",
                    },
                }}
            />
            {formState.errors.cases !== undefined && (
                <Box sx={{ color: "red", fontSize: "12px", ml: 1, mt: 1 }}>
                    You must select at least one case.
                </Box>
            )}
        </>
    );
};

export default AssignCasesAutocomplete;
